/**
 * AI Chatbot Component
 * 사용자의 건강 관련 질문에 대해 AI가 답변하는 컴포넌트
 * 로그인 시: 건강 조언 기반 AI 챗봇
 * 비로그인 시: 기본 질문 예시
 */

import React, { useRef, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";

// 기본 질문 예시 (API 호출 실패 시 사용)
const DEFAULT_QUESTIONS = [
  "최근에 혈압이 좀 높은 편인데 어떻게 해야 할까요?",
  "걷기를 더 해야 하나요?",
  "약을 복용하고 있는데 식단 관리는 어떻게 하면 좋을까요?",
  "최근 기록을 보니 뭘 우선으로 챙겨야 하나요?",
];

type ChatMessage = {
  id: string;
  role: "user" | "ai";
  text: string;
  timestamp: Date;
  periodStart?: string;
  periodEnd?: string;
};

interface AIChatbotComponentProps {
  onSymptomClick?: (symptomText: string) => void;
  isLoggedIn?: boolean;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

export const AIChatbotComponent: React.FC<AIChatbotComponentProps> = ({
  onSymptomClick,
  isLoggedIn: externalIsLoggedIn,
}) => {
  const { isLoggedIn: contextIsLoggedIn, user } = useAuth();
  const isLoggedIn = externalIsLoggedIn !== undefined ? externalIsLoggedIn : contextIsLoggedIn;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recommendedQuestions, setRecommendedQuestions] = useState<string[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initMessageRef = useRef<HTMLDivElement>(null);

  // 초기 메시지 설정
  useEffect(() => {
    if (isLoggedIn && user) {
      // 로그인 상태: 인사말만 표시
      setMessages([
        {
          id: "init-1",
          role: "ai",
          text: `안녕하세요! 저는 허비서AI 건강 어시스턴트입니다. 최근 건강 데이터를 기반으로 맞춤형 건강 조언을 해드립니다.\n무엇을 궁금해하시나요?`,
          timestamp: new Date(),
        },
      ]);
      
      // 동적 질문 예시 로드
      const loadDynamicQuestions = async () => {
        setLoadingQuestions(true);
        try {
          const response = await fetch(`${API_BASE_URL}/api/ai-chat/recommended-questions`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("authToken") || ""}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && Array.isArray(data.data) && data.data.length > 0) {
              setRecommendedQuestions(data.data);
              console.log('[AIChatbotComponent] Loaded dynamic questions:', data.data.length);
              setLoadingQuestions(false);
              return;
            }
          }
        } catch (err) {
          console.warn('[AIChatbotComponent] Failed to load dynamic questions:', err);
        }
        
        // API 실패 시 기본 질문 사용
        setRecommendedQuestions(DEFAULT_QUESTIONS);
        setLoadingQuestions(false);
      };
      
      loadDynamicQuestions();
    } else {
      // 비로그인 상태: 예시 질문과 설명
      setMessages([
        {
          id: "init-1",
          role: "ai",
          text: `안녕하세요! 저는 허비서AI 건강 어시스턴트입니다.\n\n로그인하면 당신의 최근 건강 데이터를 바탕으로 더욱 맞춤형 건강 조언을 받을 수 있습니다.\n\n일단 한번 물어보시겠어요? 😊`,
          timestamp: new Date(),
        },
      ]);
      setRecommendedQuestions([]);
    }
    setError(null);
  }, [isLoggedIn, user]);

  // 기존 질문/답변 이력 로드 (초기 메시지 앞에 배치)
  useEffect(() => {
    if (!isLoggedIn || !user) return;

    const loadChatHistory = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/ai-chat/history`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken") || ""}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.data) && data.data.length > 0) {
            const historyMessages: ChatMessage[] = [];
            
            // 기존 질문/답변을 메시지 형식으로 변환 (API는 DESC 순서이므로 역순으로 변환)
            data.data.reverse().forEach((query: any) => {
              // 질문 메시지 추가
              historyMessages.push({
                id: `user-${query.id}`,
                role: "user",
                text: query.question,
                timestamp: new Date(query.created_at),
              });
              
              // 답변 메시지 추가
              historyMessages.push({
                id: `ai-${query.id}`,
                role: "ai",
                text: query.response,
                timestamp: new Date(query.created_at),
                periodStart: query.health_advice_period_start,
                periodEnd: query.health_advice_period_end,
              });
            });
            
            // 초기 메시지 앞에 기존 이력 추가 (초기 메시지는 이미 messages에 있음)
            setMessages((prev) => [...historyMessages, ...prev]);
            console.log('[AIChatbotComponent] Loaded chat history:', historyMessages.length, 'messages');
          }
          setHistoryLoaded(true);
        }
      } catch (err) {
        console.warn('[AIChatbotComponent] Failed to load chat history:', err);
        setHistoryLoaded(true);
      }
    };

    loadChatHistory();
  }, [isLoggedIn, user]);

  // 기존 데이터 로드 완료 후 초기 메시지(인사말) 위치로 스크롤
  useEffect(() => {
    if (historyLoaded && initMessageRef.current) {
      setTimeout(() => {
        initMessageRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 50);
    }
  }, [historyLoaded]);

  // 새로운 메시지 수신 시 자동으로 최하단으로 스크롤 (초기 메시지 제외)
  // 초기 메시지(messages.length === 1)일 때는 스크롤하지 않고,
  // 사용자 질문 후 AI 응답이 추가되어야만(messages.length > 1) 스크롤
  useEffect(() => {
    if (messages.length > 1) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // API에 질문 전송 및 답변 받기
  const handleSendMessage = async () => {
    const trimmedInput = input.trim();
    if (!trimmedInput) return;

    // 사용자 메시지 추가
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text: trimmedInput,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);
    setError(null);

    try {
      // 로그인 상태에서만 API 호출
      if (isLoggedIn && user) {
        const response = await fetch(`${API_BASE_URL}/api/ai-chat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken") || ""}`,
          },
          credentials: "include",
          body: JSON.stringify({
            question: trimmedInput,
          }),
        });

        if (!response.ok) {
          throw new Error(`API Error: ${response.status}`);
        }

        const data = await response.json();

        if (data.success && data.data) {
          const aiMessage: ChatMessage = {
            id: `ai-${data.data.id}`,
            role: "ai",
            text: data.data.response,
            timestamp: new Date(data.data.created_at),
            periodStart: data.data.health_advice_period_start,
            periodEnd: data.data.health_advice_period_end,
          };

          setMessages((prev) => [...prev, aiMessage]);
        } else {
          throw new Error(data.error || "Failed to get AI response");
        }
      } else {
        // 비로그인 상태: 더미 응답
        const dummyResponse = `이 기능을 사용하려면 로그인이 필요합니다.\n로그인하면 당신의 건강 데이터를 기반으로 회신받을 수 있습니다.`;

        const aiMessage: ChatMessage = {
          id: `ai-${Date.now()}`,
          role: "ai",
          text: dummyResponse,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, aiMessage]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "알 수 없는 오류 발생";
      setError(errorMessage);

      const errorResponse: ChatMessage = {
        id: `error-${Date.now()}`,
        role: "ai",
        text: `죄송합니다. 응답을 생성하는 중 오류가 발생했습니다: ${errorMessage}\n잠시 후 다시 시도해주세요.`,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorResponse]);
    } finally {
      setLoading(false);
    }
  };

  // 권장 질문 선택
  const handleQuickQuestion = (question: string) => {
    setInput(question);
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* 채팅 메시지 영역 (스크롤 가능) */}
      <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3">
        {messages.map((msg) => (
          <div
            key={msg.id}
            ref={msg.id === "init-1" ? initMessageRef : null}
            className={`flex items-start gap-2 ${
              msg.role === "ai" ? "justify-start" : "justify-end"
            }`}
          >
            {msg.role === "ai" && (
              <div className="w-7 h-7 rounded-lg bg-violet-600 text-white flex items-center justify-center flex-shrink-0 shadow-sm text-[15px]">
                🤖
              </div>
            )}

            <div className="flex flex-col max-w-[78%] gap-0.5">
              <div
                className={`rounded-2xl px-3 py-2 whitespace-pre-line text-[13px] leading-relaxed ${
                  msg.role === "ai"
                    ? "bg-slate-100 text-slate-800 rounded-tl-sm"
                    : "bg-violet-50 text-slate-900 rounded-tr-sm"
                }`}
              >
                {msg.text}
              </div>
              {msg.id !== "init-1" && (
                <div className={`text-[10px] px-1 ${
                  msg.role === "ai" 
                    ? "text-slate-400 text-left" 
                    : "text-slate-500 text-right"
                }`}>
                  {new Date(msg.timestamp).toLocaleString("ko-KR", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit"
                  }).replace(" ", " ")}
                </div>
              )}
            </div>

            {msg.role === "user" && (
              <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 text-[15px]">
                💬
              </div>
            )}
          </div>
        ))}

        {/* 권장 질문 (사용자가 현재 세션에서 새로운 질문을 하지 않았을 때) */}
        {(() => {
          // 현재 세션의 새로운 질문이 있는지 확인
          // 초기 로드된 이력: id = "user-{uuid}" (uuid는 하이픈 포함)
          // 새로운 질문: id = "user-{timestamp}" (숫자만)
          const hasNewUserQuestion = messages.some(
            msg => msg.role === "user" && /^user-\d+$/.test(msg.id)
          );
          
          return !hasNewUserQuestion && isLoggedIn && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-[11px] text-slate-500 mb-2 font-semibold">
                {loadingQuestions ? "질문 로딩 중..." : "질문 예시:"}
              </p>
              {loadingQuestions ? (
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></span>
                  <span
                    className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  ></span>
                  <span
                    className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  ></span>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {recommendedQuestions.map((q, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleQuickQuestion(q)}
                      className="inline-flex items-center rounded-full bg-violet-50 border border-violet-100 px-3 py-1.5 text-[10px] text-violet-700 font-medium hover:bg-violet-100 transition-colors"
                    >
                      <span className="mr-1 text-[12px]">💡</span>
                      <span>{q}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* 로딩 중 표시 */}
        {loading && (
          <div className="flex items-center gap-2 justify-start">
            <div className="w-7 h-7 rounded-lg bg-violet-600 text-white flex items-center justify-center flex-shrink-0 shadow-sm text-[15px]">
              🤖
            </div>
            <div className="bg-slate-100 text-slate-800 rounded-2xl rounded-tl-sm px-3 py-2 flex gap-1">
              <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></span>
              <span
                className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"
                style={{ animationDelay: "0.1s" }}
              ></span>
              <span
                className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"
                style={{ animationDelay: "0.2s" }}
              ></span>
            </div>
          </div>
        )}

        {/* 에러 표시 */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-[11px]">
            ⚠️ {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* 입력 영역 */}
      <div className="border-t border-slate-200 p-3 bg-slate-50">
        <div className="flex gap-2">
          <input
            className="flex-1 h-11 rounded-full border-2 border-violet-100 bg-white px-4 text-[13px] outline-none focus:border-violet-300 placeholder-slate-400"
            placeholder={
              isLoggedIn
                ? "건강에 대해 궁금한 점을 물어보세요..."
                : "로그인 후 사용 가능합니다."
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !loading) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            disabled={loading || !isLoggedIn}
          />
          <button
            onClick={handleSendMessage}
            disabled={loading || !input.trim() || !isLoggedIn}
            className="w-11 h-11 rounded-full bg-violet-600 text-white flex items-center justify-center hover:bg-violet-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors shadow-sm flex-shrink-0"
            title="전송"
          >
            {loading ? (
              <span className="animate-spin">⏳</span>
            ) : (
              <span className="text-[18px]">📤</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIChatbotComponent;
